// ─────────────────────────────────────────────
// KLOTHIQ — Audit History Page (wired)
// ─────────────────────────────────────────────

import { Input } from '@/components/ui/FormControls';
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '@/components/common/PageHeader';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import EmptyState from '@/components/common/EmptyState';
import ExportActions from '@/components/common/ExportActions';
import { Shield, Search } from 'lucide-react';

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  UPDATE: 'bg-blue-100 text-blue-700 border-blue-200',
  DELETE: 'bg-red-100 text-red-700 border-red-200',
  STATUS_CHANGE: 'bg-amber-100 text-amber-700 border-amber-200',
  VOID: 'bg-red-100 text-red-700 border-red-200',
};

export default function AuditHistoryPage() {
  const { organization } = useAuth();
  const orgId = organization?.id;
  const [search, setSearch] = useState('');
  const [tableFilter, setTableFilter] = useState('all');

  const {
    data: logs,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['audit_logs', orgId, tableFilter, search],
    queryFn: async () => {
      let query = supabase
        .from('audit_logs')
        .select('*')
        .eq('organization_id', orgId!)
        .order('created_at', { ascending: false })
        .limit(200);

      if (tableFilter !== 'all') {
        query = query.eq('table_name', tableFilter);
      }
      if (search.trim()) {
        query = query.ilike('action', `%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!orgId,
    staleTime: 1000 * 30,
  });

  const TABLE_OPTIONS = [
    'all',
    'purchases',
    'sales',
    'payments',
    'cash_transactions',
    'stock_movements',
    'products',
  ];

  return (
    <div>
      <PageHeader
        title="Audit History"
        description="Immutable log of all business actions"
        actions={
          <ExportActions
            excelOptions={() => ({
              fileName: `Audit_History_${new Date().toISOString().split('T')[0]}`,
              sheetName: 'AuditLogs',
              headers: ['Timestamp', 'Action', 'Table', 'Record ID', 'Details'],
              data: (logs ?? []).map((l) => [
                new Date(l.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
                l.action,
                l.table_name,
                l.record_id ?? '—',
                JSON.stringify({ before: l.old_data, after: l.new_data, details: l.metadata }),
              ]),
            })}
            pdfOptions={() => ({
              fileName: `Audit_History_${new Date().toISOString().split('T')[0]}`,
              title: 'System Audit History Log',
              subtitle: `Exported on ${new Date().toLocaleDateString('en-IN')}`,
              headers: ['Timestamp', 'Action', 'Table', 'Record ID'],
              rows: (logs ?? []).map((l) => [
                new Date(l.created_at).toLocaleString('en-IN', {
                  timeZone: 'Asia/Kolkata',
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                }),
                l.action,
                l.table_name,
                l.record_id ? `${l.record_id.slice(0, 8)}…` : '—',
              ]),
            })}
          />
        }
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex overflow-x-auto rounded-md border border-border bg-muted/30 p-0.5">
          {TABLE_OPTIONS.map((t) => (
            <button
              key={t}
              onClick={() => setTableFilter(t)}
              className={`whitespace-nowrap rounded px-3 py-1.5 text-xs font-medium transition-colors capitalize ${
                tableFilter === t
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="audit-search"
            type="text"
            placeholder="Filter by action…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-9 w-40 rounded-md border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            helpLabel="Search records"
          />
        </div>
      </div>

      {isLoading && <LoadingState message="Loading audit log…" />}
      {error && <ErrorState message={(error as Error).message} onRetry={refetch} />}

      {!isLoading && !error && (!logs || logs.length === 0) && (
        <EmptyState
          icon={<Shield className="h-12 w-12" />}
          title="No audit entries"
          description="Activity will appear here as you use the system"
        />
      )}

      {!isLoading && !error && logs && logs.length > 0 && (
        <>
          {/* Desktop */}
          <div className="hidden overflow-hidden rounded-lg border border-border bg-card shadow-sm md:block">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Timestamp
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Action
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Table</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Record ID
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-muted/20">
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString('en-IN', {
                        timeZone: 'Asia/Kolkata',
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${ACTION_COLORS[log.action] ?? 'bg-muted text-muted-foreground'}`}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 capitalize text-muted-foreground">
                      {log.table_name?.replace(/_/g, ' ')}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                      {log.record_id?.slice(0, 8)}…
                    </td>
                    <td className="px-4 py-2.5 max-w-xs text-xs text-muted-foreground">
                      <details>
                        <summary className="min-h-11 cursor-pointer break-words py-3">
                          {log.metadata?.action ?? 'View audit details'}
                        </summary>
                        <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-muted p-3">
                          {JSON.stringify(
                            { before: log.old_data, after: log.new_data, details: log.metadata },
                            null,
                            2,
                          )}
                        </pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card shadow-sm md:hidden">
            {logs.map((log) => (
              <div key={log.id} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <span
                    className={`inline-flex rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${ACTION_COLORS[log.action] ?? 'bg-muted text-muted-foreground'}`}
                  >
                    {log.action}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {new Date(log.created_at).toLocaleString('en-IN', {
                      timeZone: 'Asia/Kolkata',
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <p className="mt-1 text-sm capitalize text-foreground">
                  {log.table_name?.replace(/_/g, ' ')}
                </p>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                  <details>
                    <summary className="min-h-11 cursor-pointer py-3">
                      {log.metadata?.action ?? 'View audit details'}
                    </summary>
                    <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-muted p-3">
                      {JSON.stringify(
                        { before: log.old_data, after: log.new_data, details: log.metadata },
                        null,
                        2,
                      )}
                    </pre>
                  </details>
                </p>
              </div>
            ))}
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            {logs.length} entries shown (max 200)
          </p>
        </>
      )}
    </div>
  );
}
